import { FC, useState, useMemo, useEffect } from 'react';
import Box from '@amzn/awsui-components-core/box';
import Header from '@amzn/awsui-components-core/header';
import Table, { TableProps } from '@amzn/awsui-components-core/table';
import TextFilter from '@amzn/awsui-components-core/text-filter';
import Pagination from '@amzn/awsui-components-core/pagination';
import Link from '@amzn/awsui-components-core/link';
import { CampaignTableRow } from 'src/@types/valueMatching/campaign';
import StatusBadge from 'src/components/atoms/StatusBadge';
import CampaignActionBar from 'src/components/molecules/CampaignActionBar';
import { useTranslate } from 'src/hooks/useTranslate';

const CAMPAIGN_TABLE_BUNDLE = 'organisms.campaignTable';

const PAGE_SIZE = 15;

/** Fields that contain numeric values and should be sorted numerically rather than lexicographically */
const NUMERIC_SORT_FIELDS = new Set<string>(['fundingRemaining']);

/** Safely converts any value to lowercase string for filtering */
const safeString = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value).toLowerCase();

interface CampaignTableProps {
  campaigns: CampaignTableRow[];
  campaignTitle: string;
  loading?: boolean;
  onCampaignClick: (campaignId: string, campaignVersion: string) => void;
  onCreateClick: () => void;
  onModifyClick: (campaignId: string, campaignVersion: string, status: string) => void;
  onCancelClick: (campaignId: string, campaignVersion: string, status: string, title: string) => void;
}

const CampaignTable: FC<CampaignTableProps> = ({
  campaigns,
  campaignTitle,
  loading = false,
  onCampaignClick,
  onCreateClick,
  onModifyClick,
  onCancelClick,
}) => {
  const { translate } = useTranslate(CAMPAIGN_TABLE_BUNDLE);

  const [filterText, setFilterText] = useState('');
  const [selectedItems, setSelectedItems] = useState<CampaignTableRow[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortingColumn, setSortingColumn] = useState<TableProps.SortingColumn<CampaignTableRow>>({
    sortingField: 'lastModified',
  });
  const [sortingDescending, setSortingDescending] = useState(true);

  // Reset page and selection when the campaigns data changes (e.g. navigating to a different campaign type)
  useEffect(() => {
    setCurrentPage(1);
    setSelectedItems([]);
  }, [campaigns]);

  // Filter
  const filteredCampaigns = useMemo(() => {
    if (!filterText) return campaigns;
    const lowerFilter = filterText.toLowerCase();
    return campaigns.filter(
      (row) =>
        safeString(row.id).includes(lowerFilter) ||
        safeString(row.title).includes(lowerFilter) ||
        safeString(row.status).includes(lowerFilter) ||
        safeString(row.createdBy).includes(lowerFilter) ||
        safeString(row.merchant).includes(lowerFilter) ||
        safeString(row.lastModifiedBy).includes(lowerFilter)
    );
  }, [campaigns, filterText]);

  // Sort
  const sortedCampaigns = useMemo(() => {
    if (!sortingColumn.sortingField) return filteredCampaigns;
    const field = sortingColumn.sortingField as keyof CampaignTableRow;
    const dir = sortingDescending ? -1 : 1;
    return [...filteredCampaigns].sort((a, b) => {
      const aVal = a[field];
      const bVal = b[field];
      if (aVal === bVal) return 0;
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      if (NUMERIC_SORT_FIELDS.has(field)) {
        return dir * (Number(aVal) - Number(bVal));
      }
      return dir * String(aVal).localeCompare(String(bVal));
    });
  }, [filteredCampaigns, sortingColumn, sortingDescending]);

  // Paginate
  const paginatedCampaigns = sortedCampaigns.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const handleSortingChange: TableProps['onSortingChange'] = ({ detail }) => {
    setSortingColumn(detail.sortingColumn);
    setSortingDescending(detail.isDescending ?? false);
    setCurrentPage(1);
  };

  const COLUMN_DEFINITIONS = useMemo<TableProps.ColumnDefinition<CampaignTableRow>[]>(() => [
    {
      id: 'id',
      header: translate('column_campaign_id'),
      cell: (item) => (
        <Link onFollow={() => onCampaignClick(item.id, item.version)}>{item.id}</Link>
      ),
      sortingField: 'id',
    },
    {
      id: 'title',
      header: translate('column_title'),
      cell: (item) => item.title || '-',
      sortingField: 'title',
    },
    {
      id: 'fundingRemaining',
      header: translate('column_funding_remaining'),
      cell: (item) => item.fundingRemaining || '-',
      sortingField: 'fundingRemaining',
    },
    {
      id: 'merchant',
      header: translate('column_merchant'),
      cell: (item) => item.merchant || '-',
      sortingField: 'merchant',
    },
    {
      id: 'startTime',
      header: translate('column_start_time'),
      cell: (item) => item.startTime || '-',
      sortingField: 'startTime',
    },
    {
      id: 'endTime',
      header: translate('column_end_time'),
      cell: (item) => item.endTime || '-',
      sortingField: 'endTime',
    },
    {
      id: 'createdBy',
      header: translate('column_created_by'),
      cell: (item) => item.createdBy || '-',
      sortingField: 'createdBy',
    },
    {
      id: 'lastModified',
      header: translate('column_last_modified'),
      cell: (item) => item.lastModified || '-',
      sortingField: 'lastModified',
    },
    {
      id: 'lastModifiedBy',
      header: translate('column_last_modified_by'),
      cell: (item) => item.lastModifiedBy || '-',
      sortingField: 'lastModifiedBy',
    },
    {
      id: 'status',
      header: translate('column_status'),
      cell: (item) => <StatusBadge status={item.status} />,
      sortingField: 'status',
    },
  ], [translate, onCampaignClick]);

  return (
    <Table
      columnDefinitions={COLUMN_DEFINITIONS}
      items={paginatedCampaigns}
      loading={loading}
      loadingText={translate('loading_text')}
      sortingColumn={sortingColumn}
      sortingDescending={sortingDescending}
      onSortingChange={handleSortingChange}
      selectionType="single"
      selectedItems={selectedItems}
      onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
      trackBy={(item) => `${item.id}-${item.version}`}
      ariaLabels={{
        selectionGroupLabel: 'Campaign selection',
        allItemsSelectionLabel: () => 'Select all campaigns',
        itemSelectionLabel: (_data, row) => `Select campaign ${row.id}`,
      }}
      empty={
        <Box textAlign="center" color="inherit">
          <b>{translate('no_campaigns')}</b>
        </Box>
      }
      filter={
        <TextFilter
          filteringPlaceholder={translate('search_placeholder')}
          filteringText={filterText}
          filteringAriaLabel="Filter campaigns"
          onChange={({ detail }) => {
            setFilterText(detail.filteringText);
            setCurrentPage(1);
            setSelectedItems([]);
          }}
        />
      }
      header={
        <Header
          actions={
            <CampaignActionBar
              hasSelection={selectedItems.length > 0}
              onCreateClick={onCreateClick}
              onModifyClick={() => {
                if (selectedItems[0]) onModifyClick(selectedItems[0].id, selectedItems[0].version, selectedItems[0].status);
              }}
              onCancelClick={() => {
                if (selectedItems[0]) onCancelClick(selectedItems[0].id, selectedItems[0].version, selectedItems[0].status, selectedItems[0].title);
              }}
            />
          }
          counter={`(${filteredCampaigns.length})`}
        >
          {translate('table_header', { campaignType: campaignTitle })}
        </Header>
      }
      pagination={
        <Pagination
          currentPageIndex={currentPage}
          pagesCount={Math.max(1, Math.ceil(sortedCampaigns.length / PAGE_SIZE))}
          onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
          ariaLabels={{
            nextPageLabel: 'Next page',
            previousPageLabel: 'Previous page',
            pageLabel: (pageNumber) => `Page ${pageNumber}`,
          }}
        />
      }
    />
  );
};

export default CampaignTable;
